/**
 * Read every row of a query, not just the first page PostgREST will hand over.
 *
 * PostgREST answers an unpaged select with at most 1000 rows, HTTP **200**, and
 * `Content-Range: 0-999/*`. There is no error and no exception: supabase-js
 * cannot distinguish that reply from a table that genuinely holds 1000 rows, so
 * an unpaged read of a growing table silently starts losing data the day it
 * crosses the cap, and every total computed from it quietly gets smaller.
 * Measured against this project's own API: a select over a 21,004-row table
 * returned exactly 1,000 rows with `error` null.
 *
 * Two rules for callers:
 *
 * 1. **Order by something unique.** Paging an `ORDER BY` whose key has
 *    duplicates lets the database order rows within a key differently per page,
 *    which drops rows at page boundaries and repeats others. `id` is the
 *    reliable tie-break; this file cannot add it for you because it never sees
 *    the query.
 * 2. **Decide what a partial answer means.** On failure the helper returns the
 *    rows it managed to read *and* the error, because only the call site knows
 *    whether a short list may be shown as though it were complete.
 *
 * @module services/supabasePaging
 */

import { logError } from "./logger";

/** PostgREST's per-response row cap, and therefore our page size. */
export const PAGE_SIZE = 1000;

/**
 * Ceiling on pages per read. Termination depends on the server honouring
 * `range`; one that ignores it returns a full page forever, and an infinite
 * loop in the browser is worse than a short answer. 100 pages is 100,000 rows —
 * far beyond any read in this app, so hitting it means something is wrong.
 */
export const MAX_PAGES = 100;

/** What one page of a supabase read settles to. */
export interface Page<T> {
  data: T[] | null;
  error: unknown;
}

export interface PagedResult<T> {
  rows: T[];
  /** null when the whole table was read; otherwise why the walk stopped early. */
  error: unknown;
}

/**
 * The words for a read's `error`, for the message a caller throws: the
 * PostgREST error's `message`, else the value as text. One rule for the
 * Executive Overview's and the Channel Deep-dive's readers.
 */
export const errorMessageOf = (error: unknown): string =>
  (error as { message?: string } | null)?.message ?? String(error);

/**
 * Walk a query page by page until it returns a short page.
 *
 * @param page Builds and awaits one page. Both bounds are inclusive, matching
 *   `.range(from, to)`.
 * @param label Names the read in the log when the page cap trips.
 */
export const readAllRows = async <T>(
  page: (from: number, to: number) => PromiseLike<Page<T>>,
  label = "supabase read",
): Promise<PagedResult<T>> => {
  const rows: T[] = [];

  for (let pageIndex = 0; pageIndex < MAX_PAGES; pageIndex += 1) {
    const from = pageIndex * PAGE_SIZE;
    const { data, error } = await page(from, from + PAGE_SIZE - 1);
    if (error) return { rows, error };

    const batch = data ?? [];
    rows.push(...batch);
    // A short page is the only proof there is nothing left. A full one is
    // ambiguous even when it is the last, so the walk costs one extra request
    // whenever the total lands on a page boundary.
    if (batch.length < PAGE_SIZE) return { rows, error: null };
  }

  const error = new Error(
    `${label}: stopped after ${MAX_PAGES} pages (${rows.length} rows) — the query never returned a short page`,
  );
  logError("supabasePaging", "Page cap reached; result is incomplete", error);
  return { rows, error };
};
